import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  serializeEditGraph,
  DEFAULT_CAPTION_ANCHOR_Y_MILLI,
  type CaptionAuthoredStyle,
} from "@desk/core";
import {
  CONSTRAINT_LABELS,
  evaluateChallengeConstraints,
} from "../game/challenge/constraints.js";
import { isCastingUnlocked, isCaptionDeepUnlocked, isSoundDeepUnlocked } from "../game/unlocks.js";
import { publishBlockedReason } from "../game/reducer.js";
import { SHIFT_PACK_ORDER } from "../content/registry.js";
import { shouldShowPerfPanel } from "../perf/perf-stats.js";
import { PerfPanel } from "../perf/PerfPanel.js";
import type { GameController } from "../shell/hooks/useGameController.js";
import { useDeskSession } from "../shell/hooks/useDeskSession.js";
import { layoutModeForWidth, isTouchEditor } from "../shell/lib/layout.js";
import {
  formatTicksAsTime,
  editTickToSourceTick,
  sourceTickToEditTick,
  countJumpCuts,
} from "../shell/lib/timeline.js";
import { focusBeatId } from "../shell/lib/focus-frame.js";
import {
  focusPanCrop,
  isFocusZoomed,
  focusCropForSubject,
  focusCropForZoom,
  focusZoomPctFromCrop,
  clampFocusZoomPct,
  subjectRectToScreenPct,
  FOCUS_ZOOM_MIN_PCT,
  FOCUS_ZOOM_MAX_PCT,
  FOCUS_ZOOM_STEP_PCT,
  type FocusCrop,
} from "../shell/lib/tool-selection.js";
import { subjectsForPack, type SubjectBox } from "../content/landing/subjects.js";
import { isGrammarValid, nextOpId } from "../shell/lib/edit-helpers.js";
import { packEndTick } from "../shell/lib/caption-preview.js";
import { cycleCaptionStyleId } from "../shell/lib/caption-style-options.js";
import { sampleCreativeRenderState } from "../shell/lib/creative-render-state.js";
import { prefersReducedMotion } from "../shell/lib/audio-audition.js";
import { playThmp } from "../shell/lib/ui-thmp.js";
import { creativeComboLabel } from "../shell/lib/creative-combo.js";
import { dockToolsForPack, type ToolId } from "../shell/ui/tools.js";
import { ToolDock } from "../shell/ui/ToolDock.js";
import { CutTool } from "../shell/ui/CutTool.js";
import { FrameTool } from "../shell/ui/FrameTool.js";
import { TextTool } from "../shell/ui/TextTool.js";
import { SoundTool } from "../shell/ui/SoundTool.js";
import { LookTool } from "../shell/ui/LookTool.js";
import { DecorateTool } from "../shell/ui/DecorateTool.js";
import { CastTool } from "../shell/ui/CastTool.js";
import {
  isCreativeOptionOwned,
  resolveResultCardTheme,
  type CreativeOptionKind,
  type StoreItemId,
} from "../game/store-catalog.js";
import { CreativeOverlayLayer, type StickerEditController } from "../shell/ui/CreativeOverlay.js";
import {
  clampStickerAnchorXMilli,
  clampStickerAnchorYMilli,
  clampStickerScaleMilli,
  clampStickerRotationMilli,
} from "../shell/lib/sticker-edit.js";
import { CreativeFxLayer } from "../shell/ui/CreativeFxLayer.js";
import { SHARED_REACTION_TAGS } from "../content/creative-catalogs.js";
import {
  crowdReaction,
  editEmotionVector,
  emotionVectorChanges,
  emotionChangeReason,
  baseEmotionVector,
  EMOTION_AXIS_COLORS,
  type CrowdReaction,
  type EmotionAxisChange,
  type EmotionVector,
} from "../game/emotion-model.js";
import { landingConfigForPack } from "../content/landing/index.js";
import { crowdChromeForPack } from "../content/landing/crowds-presentation.js";
import {
  placementZoneForAnchorY,
  placementCue,
  PLACEMENT_ZONE_ANCHORS,
} from "../shell/lib/caption-placement.js";
import {
  presentationFromSession,
  restoredCaptionPos,
} from "../game/challenge/presentation.js";
import { EditorHeaderFaces } from "../shell/ui/EditorHeaderFaces.js";
import {
  fxKindsForEffectIds,
  isEffectLook,
  toggleEffectInstance,
  removeEffectInstance,
  effectIdsAtBeat,
  distinctEffectIds,
  type EffectInstance,
} from "../shell/lib/creative-fx.js";
import { ReconstructionLabel } from "../shell/ui/ReconstructionLabel.js";
import { StatsPanel, EmotionSpider } from "../shell/ui/StatsPanel.js";
import {
  StudioStoreProvider,
  type StudioStoreOpenRequest,
} from "../shell/ui/StudioStoreContext.js";
import { StudioStoreDrawer } from "../shell/ui/StudioStoreDrawer.js";
import { track } from "../game/analytics.js";

export function EditorScreen({ game }: { game: GameController }) {
  const { state, dispatch } = game;
  const edit = state.currentEdit;
  // Which story this is, 1-based. Was hardcoded to 1, so every level said
  // "LVL 1" regardless of which one you had opened.
  const levelNo = Math.max(0, SHIFT_PACK_ORDER.indexOf(edit?.packId as never)) + 1;
  // Set when Publish is refused, so the refusal is visible instead of silent.
  const [publishError, setPublishError] = useState<string | null>(null);
  const challenge = state.challenge;
  const inChallenge = Boolean(challenge && challenge.status === "editing");
  const showPerf =
    typeof window !== "undefined" && shouldShowPerfPanel(window.location.search);
  // Bridge is installed from main.tsx so it survives publish navigation.
  const mountStarted = useRef(
    typeof performance !== "undefined" ? performance.now() : 0,
  );
  const [assetLoadMs, setAssetLoadMs] = useState(0);
  const [tool, setTool] = useState<ToolId>("cut");
  // Precision Trim (session/presentation state): beats the player has trimmed to
  // their clean end, removing that beat's "poor editing" leak Doubt. Never feeds
  // the scored edit graph — only the presentation emotion vector + Clips cue.
  const [precisionTrimmedBeats, setPrecisionTrimmedBeats] = useState<string[]>([]);
  // Stackable EFFECTS (rain / lightning / focus blur) as game-side presentation
  // state: SEVERAL can be active at once, independent of the single-select filter
  // grade (the core `look` op). Never enters the scored edit graph; the FX overlay,
  // the landing vector, and the header faces all read this set. Reset per pack.
  // Effects are now PLACED per beat (scrub to a beat, tap an effect): each entry
  // is {effectId, beatId}. Several effects can share a beat and one effect can sit
  // on several beats (stackable, independent). currentBeatId (below) is the beat
  // under the playhead a tap targets.
  const [activeEffects, setActiveEffects] = useState<EffectInstance[]>([]);
  const toggleEffect = (effectId: string) =>
    setActiveEffects((cur) =>
      currentBeatId ? toggleEffectInstance(cur, effectId, currentBeatId) : cur,
    );
  const removeEffect = (effectId: string, beatId: string) =>
    setActiveEffects((cur) => removeEffectInstance(cur, effectId, beatId));
  // Presentation-only caption state (never touches the scored edit graph):
  // a dragged position override + the cosmetic reaction-tag emoji.
  const [captionPos, setCaptionPos] = useState<{ xMilli: number; yMilli: number } | null>(
    null,
  );
  // Presentation-only caption scale + rotation override (never touches the
  // scored edit graph) — committed together by the on-video bottom-left
  // combined resize+rotate handle, same pattern as `captionPos` above.
  const [captionTransform, setCaptionTransform] = useState<{
    scaleMilli: number;
    rotationMilli: number;
  } | null>(null);
  const [reactionId, setReactionId] = useState<string>("reaction-none");
  // Which sticker INSTANCE is selected — lifted here (unlimited stickers, no
  // fixed slots) so the on-video sticker with this id becomes the
  // directly-manipulable (tap-select / drag / resize / rotate / delete) one.
  // Dropping a new sticker in DecorateTool selects it; tapping any sticker on
  // the video (via the CreativeOverlay controller) keeps this in sync.
  const [selectedStickerId, setSelectedStickerId] = useState<string | null>(null);
  const [sourceOpen, setSourceOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  // One-time "tap me" pulse hint on the LVL pill until the player first opens
  // "How it's landing". Persisted so that once the player has used it, the hint
  // never reappears on reload or the next story. Presentation only.
  const [levelHintSeen, setLevelHintSeen] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem("desk.hint.landing.seen") === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    if (typeof window === "undefined" || !levelHintSeen) return;
    try {
      window.localStorage.setItem("desk.hint.landing.seen", "1");
    } catch {
      /* storage unavailable (private mode); dismissal still holds in-session */
    }
  }, [levelHintSeen]);
  // Optional pinned compact live spider beside the preview. OFF by default; the
  // LVL pill still opens the full panel. Persisted so the choice survives edits
  // and reloads. Presentation only.
  const [spiderPinned, setSpiderPinned] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem("desk.editor.spiderPinned") === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem("desk.editor.spiderPinned", spiderPinned ? "1" : "0");
    } catch {
      /* storage unavailable (private mode); toggle still works in-session */
    }
  }, [spiderPinned]);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [storeOpen, setStoreOpen] = useState(false);
  const [storeFocusItemId, setStoreFocusItemId] = useState<StoreItemId | null>(null);
  const [storeFocusTool, setStoreFocusTool] = useState<ToolId | null>(null);
  const sourceDialogRef = useRef<HTMLDialogElement>(null);
  const sourceOpenerRef = useRef<HTMLButtonElement>(null);
  const storeReturnFocusRef = useRef<HTMLElement | null>(null);
  const [storeReturnFocusSelector, setStoreReturnFocusSelector] = useState<
    string | null
  >(null);
  const studioBagBtnRef = useRef<HTMLButtonElement>(null);
  const overflowRef = useRef<HTMLDivElement>(null);
  const lastUiCueAtRef = useRef(0);
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1280,
  );
  const layout = layoutModeForWidth(width);
  const touch = isTouchEditor(layout);

  const onGraphChange = (graphJson: string) => {
    dispatch({ type: "UPDATE_EDIT", graphJson });
  };

  const session = useDeskSession({
    packId: edit?.packId,
    seed: edit?.seed,
    graphJson: edit?.graphJson,
    onGraphChange,
    unlocks: state.unlocks,
    purchases: state.purchases,
    equippedTheme: state.equippedTheme,
  });

  const constraintViolations = useMemo(() => {
    if (!inChallenge || !challenge) return [];
    return evaluateChallengeConstraints(
      session.pack,
      session.graph,
      challenge.constraintIds,
    );
  }, [inChallenge, challenge, session.pack, session.graph]);

  const hasCastSlots = (session.pack.castSlots?.length ?? 0) > 0;
  const castingUnlocked = isCastingUnlocked(state.unlocks) || state.qaBypass || inChallenge;
  const captionDeep =
    isCaptionDeepUnlocked(state.unlocks) ||
    state.purchases.includes("caption-style-reveal") ||
    state.qaBypass ||
    inChallenge;
  const soundDeep = isSoundDeepUnlocked(state.unlocks) || state.qaBypass || inChallenge;
  const soundStingPack =
    state.purchases.includes("sound-sting-pack") || state.qaBypass || inChallenge;
  const exportCardTheme = resolveResultCardTheme({
    purchases: state.purchases,
    equippedTheme: state.equippedTheme,
  });
  const precisionTrim =
    state.purchases.includes("precision-trim") || state.qaBypass || inChallenge;
  // Trims only count when the player owns Precision Trim; without it, a trimmed
  // beat still shows its leak (and the store cart to unlock the tool).
  const effectiveTrimmedBeats = useMemo(
    () => (precisionTrim ? precisionTrimmedBeats : []),
    [precisionTrim, precisionTrimmedBeats],
  );
  // Visible jump-cuts in the current edit + whether the player owns the addon
  // that softens them. Presentation only — feeds the "How it's landing" Doubt read
  // and the Clips "looks edited" cue, never scoring.
  const jumpCuts = useMemo(() => countJumpCuts(session.timeline), [session.timeline]);
  const seamlessTransitions = state.purchases.includes("seamless-transitions");
  // Challenge graphs may already contain premium IDs; campaign QA still respects ownership.
  const bypassOwnership = inChallenge;

  const dockTools = useMemo(
    () =>
      dockToolsForPack({
        packId: session.packId,
        hasCastSlots,
        castingUnlocked,
      }),
    [session.packId, hasCastSlots, castingUnlocked],
  );

  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!session.ready) return;
    setAssetLoadMs(Math.max(0, performance.now() - mountStarted.current));
  }, [session.ready]);

  // Anonymous analytics: fires once per level-editor open (mount + every pack
  // switch). No-op unless analytics is configured + enabled (see analytics.ts).
  useEffect(() => {
    track({ name: "level_opened", props: { levelId: session.packId } });
  }, [session.packId]);

  // Reset the game-side presentation levers whenever the pack/session changes.
  // When a shared "Beat My Edit" link was accepted, the sharer's `?p=` sidecar
  // (decoded on boot into `challenge.challengerPresentation`) SEEDS the levers
  // here instead of clearing them, so the reel + "How it's landing" reproduce
  // the sharer's. Folding restore into the reset keeps a single source of truth
  // and stays idempotent under React 18 StrictMode's double-invoke (no separate
  // guarded effect that a re-run reset could clobber). Presentation only.
  useEffect(() => {
    setTool("cut");
    setSourceOpen(false);
    setInfoOpen(false);
    setPanHintSeen(false);
    setSelectedStickerId(null);
    const pres =
      inChallenge && challenge ? challenge.challengerPresentation ?? null : null;
    setCaptionPos(pres ? restoredCaptionPos(pres) : null);
    setCaptionTransform(null);
    setReactionId(pres?.reactionId ?? "reaction-none");
    setPrecisionTrimmedBeats(pres ? [...pres.trims] : []);
    setActiveEffects(pres ? [...pres.effects] : []);
    // Seeding is keyed to the session pack; challenge + its sidecar are set
    // atomically with currentEdit on ACCEPT_CHALLENGE, so they are available
    // on the mount that this pack change triggers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.packId]);

  // Toggle a beat's Precision Trim (session state). Only meaningful when the
  // player owns Precision Trim; the Clips control gates the call on ownership.
  const toggleBeatTrim = (beatId: string) =>
    setPrecisionTrimmedBeats((cur) =>
      cur.includes(beatId) ? cur.filter((b) => b !== beatId) : [...cur, beatId],
    );

  const comboLabel = useMemo(
    () => creativeComboLabel(session.resolved),
    [session.resolved],
  );

  // Live reaction faces for the LVL pill, in Brief screen order (Pet Parents ·
  // Rescue Warriors [served, middle] · Eat the Rich). Driven by the emotion
  // model's crowd reaction — presentation only, never feeds scoring.
  const landingConfig = useMemo(
    () => landingConfigForPack(session.packId),
    [session.packId],
  );

  // Sticker-zone discoverability: while a sticker instance is selected, look
  // up its catalog stickerId and highlight every authored zone that has a
  // combo for THAT sticker — "drop it here to make it react" — so the player
  // discovers the mechanic without guessing. No selection = no highlights.
  const selectedStickerCatalogId = useMemo(
    () => session.resolved.stickers.find((s) => s.id === selectedStickerId)?.stickerId ?? null,
    [session.resolved.stickers, selectedStickerId],
  );
  const highlightZoneIds = useMemo(() => {
    if (!selectedStickerCatalogId) return [];
    return landingConfig.levers.stickerCombos
      .filter((c) => c.stickerId === selectedStickerCatalogId)
      .map((c) => c.zoneId);
  }, [landingConfig, selectedStickerCatalogId]);

  // Caption PLACEMENT zone — a LIGHT gameplay lever. Derived from where the
  // caption currently sits on the video (the dragged anchor Y, or the caption's
  // default lower-third anchor before any drag), so the zone, the on-video cue,
  // and the landing contribution always agree. Feeds ONLY the presentation
  // emotion vector; never the scored edit graph. The model ignores it unless the
  // reel actually carries a caption.
  const captionPlacementZone = useMemo(
    () => placementZoneForAnchorY(captionPos?.yMilli ?? DEFAULT_CAPTION_ANCHOR_Y_MILLI),
    [captionPos],
  );
  const captionPlacementCue = useMemo(
    () => placementCue(captionPlacementZone, landingConfig),
    [captionPlacementZone, landingConfig],
  );

  // First placement: a freshly added caption starts CENTERED (subject zone)
  // rather than lower-third. Presentation only — it seeds the on-video position
  // override, never the scored op's anchor (which stays default, so scoring
  // still reads an unmodified caption). Challenge sessions keep the challenger's
  // own restored position, so we skip auto-centering there.
  const captionCount = session.resolved.captions.length;
  useEffect(() => {
    if (inChallenge) return;
    if (captionPos !== null) return;
    if (captionCount === 0) return;
    setCaptionPos({ ...PLACEMENT_ZONE_ANCHORS.subject });
  }, [inChallenge, captionPos, captionCount]);

  const headerFaces = useMemo(() => {
    const vector = editEmotionVector(session.resolved, landingConfig, {
      activeEffectIds: distinctEffectIds(activeEffects),
      captionPlacement: captionPlacementZone,
    });
    // Data-driven: the pack's crowd chrome in Brief order (served crowd in the
    // middle) merged with the live landing config, so the LVL pill faces read as
    // THIS level's crowds. Presentation only; never feeds scoring.
    return crowdChromeForPack(session.packId)
      .filter((c) => landingConfig.crowds[c.id])
      .map((c) => {
        const profile = landingConfig.crowds[c.id]!;
        const served = c.id === landingConfig.servedCrowdId;
        const reaction: CrowdReaction = crowdReaction(vector, profile, served);
        return {
          key: c.id,
          label: profile.label,
          accent: c.accent,
          served,
          reaction,
        };
      });
  }, [session.resolved, session.packId, landingConfig, activeEffects, captionPlacementZone]);

  // Live reel emotion vector for the pinned spider + cause->effect readout. Same
  // model the "How it's landing" panel reads (raw-footage baseline + jump-cut
  // Doubt), so the corner spider and the readout agree with the panel. Never
  // feeds scoring.
  const baseVector = useMemo(() => baseEmotionVector(landingConfig), [landingConfig]);
  const liveVector = useMemo(
    () =>
      editEmotionVector(session.resolved, landingConfig, {
        base: baseVector,
        jumpCuts,
        seamlessTransitions,
        tickRate: session.pack.tickRate,
        precisionTrimmedBeatIds: effectiveTrimmedBeats,
        activeEffectIds: distinctEffectIds(activeEffects),
        captionPlacement: captionPlacementZone,
      }),
    [
      session.resolved,
      landingConfig,
      baseVector,
      jumpCuts,
      seamlessTransitions,
      session.pack.tickRate,
      effectiveTrimmedBeats,
      activeEffects,
      captionPlacementZone,
    ],
  );
  const liveHasSignal = useMemo(
    () => Object.values(liveVector).some((v) => v > 0.02),
    [liveVector],
  );
  const servedProfile = landingConfig.crowds[landingConfig.servedCrowdId]!;

  // Cause->effect readout: diff the PREVIOUS vs CURRENT live vector on each edit
  // and surface the 1-2 most-changed axes with direction + a plain reason. Cleared
  // when an edit does not meaningfully move the landing. Presentation only.
  const prevVectorRef = useRef<EmotionVector | null>(null);
  const [lastChanges, setLastChanges] = useState<EmotionAxisChange[]>([]);
  // The readout fades out 10s after the last landing change so it stops covering
  // the reel (and any low-placed caption) once you've read it. Any new change
  // brings it back and restarts the timer.
  const [readoutVisible, setReadoutVisible] = useState(false);
  const readoutTimerRef = useRef<number | null>(null);
  useEffect(() => {
    const prev = prevVectorRef.current;
    if (prev) {
      const changes = emotionVectorChanges(prev, liveVector);
      setLastChanges(changes);
      if (changes.length > 0) {
        setReadoutVisible(true);
        if (readoutTimerRef.current !== null) window.clearTimeout(readoutTimerRef.current);
        readoutTimerRef.current = window.setTimeout(() => setReadoutVisible(false), 10_000);
      }
    }
    prevVectorRef.current = liveVector;
  }, [liveVector]);
  useEffect(
    () => () => {
      if (readoutTimerRef.current !== null) window.clearTimeout(readoutTimerRef.current);
    },
    [],
  );

  // Soft low "thmp" on UI select/click — a tiny WebAudio synth (see ui-thmp.ts).
  // Reduced-motion doubles as a soft sound-off gate; a short throttle prevents
  // double-taps from stacking.
  const playUiCue = () => {
    if (prefersReducedMotion()) return;
    const now = performance.now();
    if (now - lastUiCueAtRef.current < 280) return;
    lastUiCueAtRef.current = now;
    playThmp();
  };

  const selectTool = (id: ToolId) => {
    if (id !== tool) playUiCue();
    setTool(id);
  };

  useEffect(() => {
    const dialog = sourceDialogRef.current;
    if (!dialog) return;
    if (sourceOpen) {
      if (!dialog.open) {
        dialog.showModal();
      }
      const closeBtn = dialog.querySelector<HTMLElement>(
        '[data-testid="close-source-notes"]',
      );
      closeBtn?.focus();
    } else if (dialog.open) {
      dialog.close();
    }
  }, [sourceOpen]);

  useEffect(() => {
    if (tool === "cast" && !hasCastSlots) setTool("cut");
    if (tool === "cast" && hasCastSlots && !castingUnlocked) setTool("cut");
  }, [tool, hasCastSlots, castingUnlocked]);

  // Overflow (⋯) menu: close on outside click / Esc. Native <details> stayed
  // open on outside click; a controlled menu gives us dismiss-on-blur + Esc.
  useEffect(() => {
    if (!overflowOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!overflowRef.current?.contains(e.target as Node)) setOverflowOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setOverflowOpen(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [overflowOpen]);

  // Reset the overflow menu when switching packs, same as the other panels.
  useEffect(() => {
    setOverflowOpen(false);
  }, [session.packId]);

  useEffect(() => {
    const onPointer = (e: PointerEvent) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      // Sound tool already auditions music/SFX — skip UI cue to avoid double-play.
      if (target.closest('[data-testid="sound-tool"]')) return;
      if (
        !target.closest(
          ".treatment-card, .headline-card-main, .preset-card, .cast-card, .beat-card, .look-card, .sticker-card, .moment-chip, .beat-pick",
        )
      ) {
        return;
      }
      playUiCue();
    };
    document.addEventListener("pointerdown", onPointer);
    return () => document.removeEventListener("pointerdown", onPointer);
    // playUiCue closes over session.pack; refresh when pack changes
  }, [session.packId]);

  const sourceTick = useMemo(() => {
    if (session.timeline.durationTicks === 0) return 0;
    return editTickToSourceTick(session.timeline, session.tick);
  }, [session.timeline, session.tick]);

  // rAF-COALESCED SCRUBBING. A fast drag on the timeline fires many onChange
  // events; each `session.seek` sets `<video>.currentTime` and awaits a real
  // frame decode (doubled by the blurred-fill copy), so issuing one per event
  // thrashes the decoder — the main cause of laggy scrubbing, even on desktop.
  // We coalesce to at most ONE seek per animation frame, always targeting the
  // LATEST value, and show the raw drag position locally so the slider thumb
  // stays smooth while the decode catches up. When the committed tick reaches
  // the drag position, control hands back to `session.tick` (no thumb jump).
  const seekFn = session.seek;
  const [dragTick, setDragTick] = useState<number | null>(null);
  const scrubRafRef = useRef<number | null>(null);
  const scrubSettleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingScrubRef = useRef<number | null>(null);
  const onScrub = useCallback(
    (tick: number) => {
      // Local drag position → the thumb follows the pointer instantly (no lag on
      // the awaited decode). The real seek is coalesced to one per frame below.
      setDragTick(tick);
      pendingScrubRef.current = tick;
      if (scrubRafRef.current == null) {
        scrubRafRef.current = requestAnimationFrame(() => {
          scrubRafRef.current = null;
          const t = pendingScrubRef.current;
          if (t != null) void seekFn(t);
        });
      }
      // Shortly after the LAST scrub input, hand the slider back to the committed
      // `session.tick`. If every seek landed, that equals the drag position (no
      // visible move); if the decode gate rejected one, the thumb snaps to the
      // real position rather than sticking where the pointer was released.
      if (scrubSettleRef.current != null) clearTimeout(scrubSettleRef.current);
      scrubSettleRef.current = setTimeout(() => setDragTick(null), 140);
    },
    [seekFn],
  );
  useEffect(
    () => () => {
      if (scrubRafRef.current != null) cancelAnimationFrame(scrubRafRef.current);
      if (scrubSettleRef.current != null) clearTimeout(scrubSettleRef.current);
    },
    [],
  );

  // The story beat under the playhead (SOURCE ticks). Placing an effect drops it
  // on THIS beat's window; the FX overlay shows only effects on the current beat.
  const currentBeatId = useMemo(() => {
    const b = session.pack.beats.find(
      (beat) => sourceTick >= beat.startTick && sourceTick < beat.endTick,
    );
    return b?.id ?? session.pack.beats[0]?.id ?? null;
  }, [session.pack.beats, sourceTick]);

  // Sticker timing: "select a sticker" snaps the playhead to its (SOURCE-tick)
  // startTick so the user sees it. Source ticks aren't always on the edited
  // timeline (e.g. inside an omitted/trimmed range) — sourceTickToEditTick
  // returns null there, and we leave the playhead where it is rather than
  // seeking somewhere the sticker isn't visible anyway.
  const onSnapPlayheadToSourceTick = useCallback(
    (srcTick: number) => {
      const editTick = sourceTickToEditTick(session.timeline, srcTick);
      if (editTick != null) void session.seek(editTick);
    },
    [session.timeline, session.seek],
  );

  const creativeOverlay = useMemo(
    () =>
      sampleCreativeRenderState({
        pack: session.pack,
        resolved: session.resolved,
        editTick: session.tick,
        sourceTick,
        seed: session.graph.seed,
        timeline: session.timeline,
      }).overlay,
    [session.pack, session.resolved, session.tick, sourceTick, session.graph.seed, session.timeline],
  );

  // Fold the presentation-only caption state (dragged position + reaction emoji)
  // onto the sampled overlay caption. This is shell/session state — it does not
  // feed the resolved edit or scoring.
  const reactionEmoji = useMemo(() => {
    const tag = SHARED_REACTION_TAGS.find((r) => r.id === reactionId);
    return tag && tag.id !== "reaction-none" ? tag.emoji : undefined;
  }, [reactionId]);

  const overlayForRender = useMemo(() => {
    const caption = creativeOverlay.caption;
    return {
      ...creativeOverlay,
      caption: {
        ...caption,
        anchorXMilli: captionPos?.xMilli ?? caption.anchorXMilli,
        anchorYMilli: captionPos?.yMilli ?? caption.anchorYMilli,
        scaleMilli: captionTransform?.scaleMilli ?? caption.scaleMilli,
        rotationMilli: captionTransform?.rotationMilli ?? caption.rotationMilli,
        reactionEmoji: caption.visible ? reactionEmoji : undefined,
      },
    };
  }, [creativeOverlay, captionPos, captionTransform, reactionEmoji]);

  const captionDraggable = tool === "text" && overlayForRender.caption.visible;

  // Cycle the current caption's STYLE prev (-1) / next (+1) through the 4
  // authored styles, re-applying the SAME headline tokens with the new style —
  // mirrors the old TextTool panel chips' `applyStyle`/`emitCaption`, now
  // driven from the on-video inline switcher (CaptionOverlay) instead. A
  // no-op with no resolved caption yet (switcher only renders once one exists).
  const cycleCaptionStyle = (dir: -1 | 1) => {
    const lastCaption = session.resolved.captions[session.resolved.captions.length - 1];
    if (!lastCaption) return;
    const tokenIds = lastCaption.tokenIds ?? [];
    if (!isGrammarValid(session.pack, tokenIds)) return;
    const currentStyle = (lastCaption.style as CaptionAuthoredStyle) ?? "documentary";
    const nextStyle = cycleCaptionStyleId(currentStyle, dir);
    track({ name: "caption_style_changed", props: { style: nextStyle } });
    const end = Math.max(1, packEndTick(session.pack) || 90);
    session.applyOp({
      kind: "text",
      opId: nextOpId("text"),
      captionId: lastCaption.captionId,
      tokenIds: [...tokenIds],
      startTick: 0,
      endTick: end,
      style: nextStyle,
      revealMode: "static",
      emphasizedTokenIds: [],
    });
  };

  // Commit the caption's presentation-only scale + rotation together — driven
  // by the on-video bottom-left combined resize+rotate handle (same
  // clamp helpers the sticker handle reuses). Never touches the scored edit
  // graph, exactly like `captionPos` above.
  const onCaptionTransform = (scaleMilli: number, rotationMilli: number) => {
    setCaptionTransform({
      scaleMilli: clampStickerScaleMilli(scaleMilli),
      rotationMilli: clampStickerRotationMilli(rotationMilli),
    });
  };

  // On-video sticker editing: while the Decorate tool is active, EVERY sticker
  // is tappable-to-select (unlimited instances, no fixed slots); only the
  // selected instance becomes directly manipulable — drag the body to move,
  // the bottom-left handle (or a pinch) to resize + rotate together, and the
  // top-right × to delete. Each edit commits the SAME `sticker` op the
  // DecorateTool picker writes (last-wins per id), preserving the untouched
  // fields; only the moved/scaled/rotated field(s) change. Clamped to the safe
  // area + core's validation window so a commit can never trip validation or
  // leave the frame.
  const commitSticker = (
    id: string,
    patch: Partial<{
      anchorXMilli: number;
      anchorYMilli: number;
      scaleMilli: number;
      rotationMilli: number;
    }>,
  ) => {
    const cur = session.resolved.stickers.find((s) => s.id === id);
    if (!cur?.stickerId) return;
    session.applyOp({
      kind: "sticker",
      opId: nextOpId("sticker"),
      id,
      stickerId: cur.stickerId,
      anchorXMilli: clampStickerAnchorXMilli(patch.anchorXMilli ?? cur.anchorXMilli),
      anchorYMilli: clampStickerAnchorYMilli(patch.anchorYMilli ?? cur.anchorYMilli),
      scaleMilli: clampStickerScaleMilli(patch.scaleMilli ?? cur.scaleMilli),
      rotationMilli: clampStickerRotationMilli(patch.rotationMilli ?? cur.rotationMilli),
      startTick: cur.startTick,
      endTick: cur.endTick,
    });
  };
  const deleteSticker = (id: string) => {
    const cur = session.resolved.stickers.find((s) => s.id === id);
    if (!cur?.stickerId) return;
    session.applyOp({
      kind: "sticker",
      opId: nextOpId("sticker-clear"),
      id,
      stickerId: null,
      anchorXMilli: cur.anchorXMilli,
      anchorYMilli: cur.anchorYMilli,
      scaleMilli: cur.scaleMilli,
      rotationMilli: cur.rotationMilli,
      startTick: 0,
      endTick: 1,
    });
    setSelectedStickerId((sel) => (sel === id ? null : sel));
  };
  const stickerEdit: StickerEditController | null =
    tool === "decorate"
      ? {
          selectedId: selectedStickerId,
          onSelect: (id) => setSelectedStickerId(id),
          onMoveCommit: (id, anchorXMilli, anchorYMilli) => {
            commitSticker(id, { anchorXMilli, anchorYMilli });
          },
          onResizeCommit: (id, scaleMilli) => {
            commitSticker(id, { scaleMilli });
          },
          onTransformCommit: (id, scaleMilli, rotationMilli) => {
            // Combined resize+rotate handle — one op, one undo step.
            commitSticker(id, { scaleMilli, rotationMilli });
          },
          onDelete: deleteSticker,
        }
      : null;

  // Focus on-video framing: the Focus tool exposes three ways to set the SAME
  // frame-crop op the renderer + "How it's landing" already read:
  //   • tappable subject BOXES that frame a key entity (quick-frame),
  //   • pinch (touch) + mouse-WHEEL zoom, and
  //   • drag-to-pan when zoomed in.
  // All three are presentation only; framing on a non-carrier subject (the cat)
  // isolates it and pushes Pity/Doubt via the config's focusCrop lever.
  const FULL_FOCUS_CROP: FocusCrop = { xMilli: 0, yMilli: 0, wMilli: 1000, hMilli: 1000 };
  const focusBeat = useMemo(
    () => focusBeatId(session.resolved, session.timeline, session.tick, session.pack),
    [session.resolved, session.timeline, session.tick, session.pack],
  );
  const focusFrame = session.resolved.frames.get(focusBeat);
  const focusCrop: FocusCrop = focusFrame?.crop ?? FULL_FOCUS_CROP;
  const focusZoomMilli = focusFrame?.zoomMilli ?? 1000;
  const focusToolActive = tool === "frame";
  const focusZoomed = isFocusZoomed(focusCrop);
  const focusPanActive = focusToolActive && focusZoomed;
  const subjects = useMemo(() => subjectsForPack(session.packId), [session.packId]);
  // The entities declared in the beat the Focus tool is acting on. Framing stamps
  // `subjectEntityId` onto that beat's frame op, and @desk/core REJECTS an op whose
  // subject isn't declared in the target beat (e.g. `ent-owner`, present only in
  // beat-idle/beat-groom). We only stamp when the subject IS declared here so the
  // crop-isolation lever can read it; otherwise we still frame the rect but leave
  // the stamp null — always valid, so tapping any subject box never throws.
  const focusBeatEntityIds = useMemo(() => {
    const beat = session.pack.beats.find((b) => b.id === focusBeat);
    return new Set(beat?.entityIds ?? []);
  }, [session.pack.beats, focusBeat]);
  const [panHintSeen, setPanHintSeen] = useState(false);
  const panRef = useRef<{
    pointerId: number;
    startClientX: number;
    startClientY: number;
    startX: number;
    startY: number;
    rectW: number;
    rectH: number;
    crop: FocusCrop;
  } | null>(null);
  const [panning, setPanning] = useState(false);
  // Live pinch state: the two active pointers + the crop/zoom the pinch started
  // from, plus the last live crop so we can commit it on release.
  const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  const pinchRef = useRef<{
    startDist: number;
    startPct: number;
    baseCrop: FocusCrop;
    subjectEntityId: string | null;
    liveCrop: FocusCrop;
    liveSubject: string | null;
  } | null>(null);

  // Commit a crop to the acting beat's frame op (same op the slider/pan write).
  const commitFocusCrop = (crop: FocusCrop, subjectEntityId: string | null) => {
    session.applyOp({
      kind: "frame",
      opId: nextOpId("frame"),
      beatId: focusBeat,
      subjectEntityId,
      crop,
      zoomMilli: focusZoomMilli,
    });
  };

  // Tapping a subject box frames that entity (crop + subjectEntityId stamp); a
  // full-frame crop clears the stamp so "Wide" reads as no subject.
  const frameSubject = (subject: SubjectBox) => {
    const crop = focusCropForSubject(subject.rect);
    // Only stamp the subject entity when it is actually declared in the target
    // beat; otherwise frame the crop with a null stamp (valid everywhere) so the
    // op never trips the core "entity not present in beat" validation.
    const stampable = isFocusZoomed(crop) && focusBeatEntityIds.has(subject.id);
    commitFocusCrop(crop, stampable ? subject.id : null);
  };
  const clearFocusFrame = () => commitFocusCrop(FULL_FOCUS_CROP, null);

  // Wheel / pinch share one zoom step: apply a new zoom percentage keeping the
  // current crop centre (focusCropForZoom), preserving the framed subject unless
  // we zoom all the way back to Wide.
  const zoomCropForPct = (pct: number, base: FocusCrop, subjectEntityId: string | null) => {
    const clamped = Math.max(FOCUS_ZOOM_MIN_PCT, Math.min(FOCUS_ZOOM_MAX_PCT, pct));
    const crop = focusCropForZoom(clamped, base);
    return {
      crop,
      subjectEntityId: clamped <= FOCUS_ZOOM_MIN_PCT ? null : subjectEntityId,
    };
  };

  // Mouse-wheel zoom: wheel up (negative deltaY) zooms IN, down zooms out, one
  // step per notch, clamped to the slider's range. A native non-passive listener
  // (see effect below) lets us preventDefault so the page never scrolls.
  const applyWheelZoom = (deltaY: number) => {
    if (deltaY === 0) return;
    const curPct = focusZoomPctFromCrop(focusCrop);
    const dir = deltaY < 0 ? 1 : -1;
    const nextPct = curPct + dir * FOCUS_ZOOM_STEP_PCT;
    if (Math.max(FOCUS_ZOOM_MIN_PCT, Math.min(FOCUS_ZOOM_MAX_PCT, nextPct)) === curPct) return;
    const { crop, subjectEntityId } = zoomCropForPct(
      nextPct,
      focusCrop,
      focusFrame?.subjectEntityId ?? null,
    );
    commitFocusCrop(crop, subjectEntityId);
  };

  // Keep a ref to the latest wheel handler so the native listener always sees the
  // current crop without re-attaching on every render.
  const wheelHandlerRef = useRef(applyWheelZoom);
  wheelHandlerRef.current = applyWheelZoom;
  const reelShellRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = reelShellRef.current;
    if (!el || !focusToolActive) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      wheelHandlerRef.current(e.deltaY);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [focusToolActive]);

  const beginPan = (e: React.PointerEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    e.preventDefault();
    setPanHintSeen(true);
    panRef.current = {
      pointerId: e.pointerId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      startX: focusCrop.xMilli,
      startY: focusCrop.yMilli,
      rectW: rect.width,
      rectH: rect.height,
      crop: focusCrop,
    };
    setPanning(true);
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* pointer not capturable (synthetic events); drag still works */
    }
  };

  const onFocusPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!focusToolActive) return;
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointersRef.current.size >= 2) {
      // Two fingers down -> pinch-zoom. Cancel any in-flight single-finger pan.
      if (panRef.current) {
        panRef.current = null;
        setPanning(false);
      }
      const pts = [...pointersRef.current.values()];
      const startDist = Math.hypot(pts[0]!.x - pts[1]!.x, pts[0]!.y - pts[1]!.y) || 1;
      setPanHintSeen(true);
      pinchRef.current = {
        startDist,
        startPct: focusZoomPctFromCrop(focusCrop),
        baseCrop: focusCrop,
        subjectEntityId: focusFrame?.subjectEntityId ?? null,
        liveCrop: focusCrop,
        liveSubject: focusFrame?.subjectEntityId ?? null,
      };
      e.preventDefault();
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* not capturable */
      }
      return;
    }
    if (focusPanActive) beginPan(e);
  };

  const onFocusPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (pointersRef.current.has(e.pointerId)) {
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    }
    const pinch = pinchRef.current;
    if (pinch && pointersRef.current.size >= 2) {
      e.preventDefault();
      const pts = [...pointersRef.current.values()];
      const dist = Math.hypot(pts[0]!.x - pts[1]!.x, pts[0]!.y - pts[1]!.y) || 1;
      const nextPct = clampFocusZoomPct(pinch.startPct * (dist / pinch.startDist));
      const { crop, subjectEntityId } = zoomCropForPct(
        nextPct,
        pinch.baseCrop,
        pinch.subjectEntityId,
      );
      pinch.liveCrop = crop;
      pinch.liveSubject = subjectEntityId;
      session.adapter.setLiveFrameOverride({
        kind: "frame",
        opId: "frame-pinch-live",
        beatId: focusBeat,
        subjectEntityId,
        crop,
        zoomMilli: focusZoomMilli,
      });
      return;
    }
    const p = panRef.current;
    if (!p || p.pointerId !== e.pointerId) return;
    e.preventDefault();
    // Map pixel drag into crop (source) space so the frame tracks the finger; the
    // container shows crop.w worth of source, so a full-width drag pans crop.w.
    const dxMilli = ((e.clientX - p.startClientX) / p.rectW) * p.crop.wMilli;
    const dyMilli = ((e.clientY - p.startClientY) / p.rectH) * p.crop.hMilli;
    // Drag the video: content follows the finger, so the crop moves opposite.
    const next = focusPanCrop(p.crop, p.startX - dxMilli, p.startY - dyMilli);
    p.crop = { ...p.crop, xMilli: next.xMilli, yMilli: next.yMilli };
    p.startX = next.xMilli;
    p.startY = next.yMilli;
    p.startClientX = e.clientX;
    p.startClientY = e.clientY;
    session.adapter.setLiveFrameOverride({
      kind: "frame",
      opId: "frame-pan-live",
      beatId: focusBeat,
      subjectEntityId: focusFrame?.subjectEntityId ?? null,
      crop: next,
      zoomMilli: focusZoomMilli,
    });
  };

  const onFocusPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    pointersRef.current.delete(e.pointerId);
    try {
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
    } catch {
      /* no active capture; ignore */
    }
    const pinch = pinchRef.current;
    if (pinch) {
      // End the pinch once fewer than two fingers remain; commit the live crop.
      if (pointersRef.current.size < 2) {
        session.adapter.setLiveFrameOverride(null);
        pinchRef.current = null;
        commitFocusCrop(pinch.liveCrop, pinch.liveSubject);
      }
      return;
    }
    const p = panRef.current;
    if (!p || p.pointerId !== e.pointerId) return;
    panRef.current = null;
    setPanning(false);
    session.adapter.setLiveFrameOverride(null);
    commitFocusCrop(
      { xMilli: p.crop.xMilli, yMilli: p.crop.yMilli, wMilli: p.crop.wMilli, hMilli: p.crop.hMilli },
      focusFrame?.subjectEntityId ?? null,
    );
  };

  // Advanced editor FX overlay (rain / lightning / radial focus blur). Effects
  // are now beat-scoped: only those placed on the beat UNDER THE PLAYHEAD render,
  // so scrubbing/playback reveals each effect during its window. Several can
  // stack on one beat. A legacy loaded graph whose single `look` op still carries
  // an effect id is unioned in so its overlay keeps showing. Shell-only.
  const activeFxList = useMemo(() => {
    const ids = currentBeatId ? effectIdsAtBeat(activeEffects, currentBeatId) : [];
    const lookId = session.resolved.look?.treatmentId ?? null;
    if (isEffectLook(lookId) && !ids.includes(lookId!)) ids.push(lookId!);
    return fxKindsForEffectIds(ids);
  }, [activeEffects, currentBeatId, session.resolved.look?.treatmentId]);
  const [fxReducedMotion] = useState(() => prefersReducedMotion());

  const sessionWithFlags = {
    ...session,
    captionDeep,
    soundDeep,
    soundStingPack,
    exportCardTheme,
    precisionTrim,
  };

  const openStore = (req?: StudioStoreOpenRequest) => {
    if (req?.returnFocus) storeReturnFocusRef.current = req.returnFocus;
    else storeReturnFocusRef.current = studioBagBtnRef.current;
    setStoreReturnFocusSelector(req?.returnFocusSelector ?? null);
    setStoreFocusItemId(req?.itemId ?? null);
    setStoreFocusTool(req?.tool ?? null);
    setStoreOpen(true);
  };

  const studioStoreValue = useMemo(
    () => ({
      purchases: state.purchases,
      cash: state.economy.cash,
      bypassOwnership,
      inChallenge,
      openStore,
      isOptionOwned: (kind: CreativeOptionKind, optionId: string) =>
        isCreativeOptionOwned(kind, optionId, state.purchases, {
          bypass: bypassOwnership,
        }),
    }),
    [state.purchases, state.economy.cash, bypassOwnership, inChallenge],
  );


  const toolBody = (() => {
    const key = session.packId;
    switch (tool) {
      case "cut":
        return (
          <CutTool key={key} session={sessionWithFlags} />
        );
      case "frame":
        return <FrameTool key={key} session={sessionWithFlags} />;
      case "text":
        return (
          <TextTool
            key={key}
            session={sessionWithFlags}
            reactionId={reactionId}
            onReactionChange={setReactionId}
          />
        );
      case "sound":
        return (
          <SoundTool key={key} session={sessionWithFlags} playheadSourceTick={sourceTick} />
        );
      case "look":
        return (
          <LookTool
            key={key}
            session={sessionWithFlags}
            effectInstances={activeEffects}
            currentBeatId={currentBeatId}
            onToggleEffect={toggleEffect}
            onRemoveEffect={removeEffect}
          />
        );
      case "decorate":
        return (
          <DecorateTool
            key={key}
            session={sessionWithFlags}
            selectedStickerId={selectedStickerId}
            onSelectSticker={setSelectedStickerId}
            playheadSourceTick={sourceTick}
            onSnapPlayheadToSourceTick={onSnapPlayheadToSourceTick}
          />
        );
      case "cast":
        return <CastTool key={key} session={sessionWithFlags} />;
    }
  })();

  const toolDock = (
    <ToolDock tools={dockTools} active={tool} onSelect={selectTool} />
  );

  // Capture the game-side presentation levers (effects / caption placement /
  // precision-trim / reaction) so the share link's `?p=` sidecar can reproduce
  // the reel + "How it's landing". Placement is only recorded when the player
  // actually moved the caption, so a neutral edit stays sidecar-free.
  const publishPresentation = () =>
    presentationFromSession({
      effects: activeEffects,
      captionPos,
      captionPlacement: captionPos ? captionPlacementZone : null,
      reactionId,
      trims: effectiveTrimmedBeats,
    });

  const publish = () => {
    if (constraintViolations.length > 0) return;
    const graphJson = serializeEditGraph(session.graph);
    // PUBLISH answers a rejected edit with a bare `return state`, so ask FIRST
    // and say so. A button that looks live and does nothing is worse than one
    // that explains itself.
    const blocked = edit
      ? publishBlockedReason({ ...edit, graphJson })
      : "There is no cut to publish yet.";
    if (blocked) {
      setPublishError(blocked);
      return;
    }
    setPublishError(null);
    track({ name: "level_published", props: { levelId: session.packId } });
    dispatch({
      type: "PUBLISH_GRAPH",
      graphJson,
      presentation: publishPresentation(),
    });
  };

  const beatSeconds = (start: number, end: number) =>
    `${((end - start) / session.pack.tickRate).toFixed(1)}s`;

  // "Back to Desk" means the Desk hub you picked this story from, not the title
  // screen. A challenge has no roadmap behind it (OPEN_ROADMAP is a no-op while
  // one is active), so there GO_HOME stays the right exit: it dismisses it.
  const leaveEditor = () => dispatch({ type: challenge ? "GO_HOME" : "OPEN_ROADMAP" });

  return (
    <StudioStoreProvider value={studioStoreValue}>
    <div
      className={`desk-app desk-chrome creator-chrome editor-mock layout-${layout}`}
      data-testid="desk-app"
      data-active-tool={tool}
      data-layout={layout}
    >
      <header className="desk-topbar creator-topbar editor-header">
        <button
          type="button"
          className="editor-back-btn"
          data-testid="back-desk"
          aria-label="Back to Desk"
          // "Back to Desk" means the Desk hub (the roadmap you picked this story
          // from), not the title screen. A challenge has no roadmap behind it, so
          // there GO_HOME is still the right exit (it dismisses the challenge).
          onClick={() => dispatch({ type: challenge ? "GO_HOME" : "OPEN_ROADMAP" })}
        >
          <span aria-hidden>‹</span>
          <span className="sr-only">Desk</span>
        </button>
        <div className="editor-header-actions">
          <button
            type="button"
            className={`editor-level-pill${levelHintSeen ? "" : " is-hinting"}`}
            data-testid="editor-level-pill"
            data-hinting={levelHintSeen ? "0" : "1"}
            ref={studioBagBtnRef}
            aria-haspopup="dialog"
            aria-expanded={infoOpen}
            aria-label="How it's landing: tap to see how your edit is landing with each crowd"
            title="Tap to see how it's landing"
            onClick={() => {
              setLevelHintSeen(true);
              setInfoOpen(true);
            }}
          >
            <span className="editor-level-tag">LVL {levelNo}</span>
            <EditorHeaderFaces faces={headerFaces} />
            <span className="editor-level-expand" data-testid="editor-level-expand" aria-hidden>
              <span className="editor-level-expand-glyph">📊</span>
              <span className="editor-level-caret">▾</span>
            </span>
            {levelHintSeen ? null : (
              <span className="editor-level-hint" data-testid="editor-level-hint" aria-hidden>
                Tap to see how it&apos;s landing
              </span>
            )}
          </button>
          <div
            className="overflow-menu"
            data-testid="editor-overflow"
            data-open={overflowOpen ? "1" : "0"}
            ref={overflowRef}
          >
            <button
              type="button"
              className="overflow-summary"
              aria-label="More editor actions"
              aria-haspopup="menu"
              aria-expanded={overflowOpen}
              onClick={() => setOverflowOpen((o) => !o)}
            >
              <span aria-hidden>⋯</span>
            </button>
            <div className="overflow-panel" role="menu" hidden={!overflowOpen}>
              <button
                type="button"
                role="menuitem"
                disabled={!session.canUndo}
                data-testid="undo-edit"
                onClick={() => {
                  session.undo();
                  setOverflowOpen(false);
                }}
              >
                Undo
              </button>
              <button
                type="button"
                role="menuitem"
                disabled={!session.canRedo}
                data-testid="redo-edit"
                onClick={() => {
                  session.redo();
                  setOverflowOpen(false);
                }}
              >
                Redo
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  session.reset();
                  setOverflowOpen(false);
                }}
              >
                Reset
              </button>
              {!inChallenge && (
                <button
                  type="button"
                  role="menuitem"
                  data-testid="save-draft"
                  onClick={() => {
                    dispatch({ type: "UPDATE_EDIT", graphJson: serializeEditGraph(session.graph) });
                    dispatch({ type: "SAVE_DRAFT" });
                    setOverflowOpen(false);
                  }}
                >
                  Save draft
                </button>
              )}
              <button
                type="button"
                role="menuitem"
                data-testid="open-source-notes"
                ref={sourceOpenerRef}
                onClick={() => {
                  setSourceOpen(true);
                  setOverflowOpen(false);
                }}
              >
                Source notes
              </button>
            </div>
          </div>
        </div>
      </header>

      {inChallenge && challenge && (
        <div className="challenge-banner" data-testid="challenge-banner">
          <strong>Challenge constraints</strong>
          {challenge.constraintIds.length === 0 ? (
            <span className="muted">None</span>
          ) : (
            <ul>
              {challenge.constraintIds.map((id) => (
                <li key={id}>{CONSTRAINT_LABELS[id]}</li>
              ))}
            </ul>
          )}
          {constraintViolations.length > 0 && (
            <p className="error" data-testid="constraint-violations">
              {constraintViolations.map((v) => v.message).join(" · ")}
            </p>
          )}
        </div>
      )}

      <div className="editor-stage" data-testid="editor-stage">
        <main className="preview-column preview-pinned">
          <div className="reel-shell" data-testid="reel-shell" ref={reelShellRef}>
            <div className="reel-frame" ref={session.mountRef} data-testid="preview-mount" />
            <CreativeFxLayer
              fx={activeFxList}
              reducedMotion={fxReducedMotion}
              playing={session.playing}
            />
            <CreativeOverlayLayer
              overlay={overlayForRender}
              captionDraggable={captionDraggable}
              onCaptionMove={(xMilli, yMilli) => setCaptionPos({ xMilli, yMilli })}
              captionPlacementCue={captionDraggable ? captionPlacementCue : null}
              onCycleCaptionStyle={captionDraggable ? cycleCaptionStyle : undefined}
              onCaptionTransform={captionDraggable ? onCaptionTransform : undefined}
              stickerEdit={stickerEdit}
              stickerZones={landingConfig.levers.stickerZones}
              showStickerZones={Boolean(stickerEdit)}
              highlightZoneIds={highlightZoneIds}
              playheadSourceTick={sourceTick}
            />
            <ReconstructionLabel show={session.showReconstructionLabel} />
            {spiderPinned && (
              <div
                className="pinned-spider"
                data-testid="pinned-spider"
                data-served-crowd={servedProfile.id}
              >
                <span className="pinned-spider-cap" aria-hidden>
                  LIVE
                </span>
                <button
                  type="button"
                  className="pinned-spider-unpin"
                  data-testid="pinned-spider-unpin"
                  aria-label="Unpin the live spider from the preview"
                  title="Unpin live spider"
                  onClick={() => setSpiderPinned(false)}
                >
                  <span aria-hidden>📌</span>
                </button>
                <EmotionSpider
                  profile={servedProfile}
                  vector={liveVector}
                  showVector={liveHasSignal}
                  compact
                />
              </div>
            )}
            {lastChanges.length > 0 && (
              <p
                className="cause-effect-readout"
                data-testid="cause-effect-readout"
                data-axes={lastChanges.map((c) => `${c.axis}:${c.dir}`).join(",")}
                data-visible={readoutVisible ? "1" : "0"}
                role="status"
                aria-live="polite"
              >
                {lastChanges.map((c, i) => (
                  <span key={c.axis}>
                    {i > 0 ? ", " : null}
                    <span
                      className="ce-axis"
                      style={{ color: EMOTION_AXIS_COLORS[c.axis] }}
                    >
                      {c.axis}
                    </span>{" "}
                    <span
                      className="ce-arrow"
                      style={{ color: EMOTION_AXIS_COLORS[c.axis] }}
                      aria-hidden
                    >
                      {c.dir === "up" ? "↑" : "↓"}
                    </span>
                  </span>
                ))}
                <span className="ce-reason"> ({emotionChangeReason(lastChanges[0]!)})</span>
              </p>
            )}
            {focusToolActive && (
              <div
                className={`focus-pan-surface${panning ? " panning" : ""}${focusZoomed ? " zoomed" : ""}`}
                data-testid="focus-pan-surface"
                role="application"
                aria-label="Frame the video: tap a subject, pinch or scroll to zoom, drag to pan"
                data-zoomed={focusZoomed ? "1" : "0"}
                data-subject={focusFrame?.subjectEntityId ?? ""}
                data-crop-x={Math.round(focusCrop.xMilli)}
                data-crop-y={Math.round(focusCrop.yMilli)}
                data-crop-w={Math.round(focusCrop.wMilli)}
                data-crop-h={Math.round(focusCrop.hMilli)}
                onPointerDown={onFocusPointerDown}
                onPointerMove={onFocusPointerMove}
                onPointerUp={onFocusPointerUp}
                onPointerCancel={onFocusPointerUp}
              >
                {focusPanActive && !panHintSeen && (
                  <span className="focus-pan-hint" data-testid="focus-pan-hint" aria-hidden>
                    Drag to pan · scroll or pinch to zoom
                  </span>
                )}
              </div>
            )}
            {focusToolActive && subjects.length > 0 && (
              <div className="focus-subjects" data-testid="focus-subjects" data-zoomed={focusZoomed ? "1" : "0"}>
                {/* Markers track the crop: at Wide their rect is the full-frame
                    placement; when zoomed they SHIFT + SCALE with the crop window
                    (see subjectRectToScreenPct) so they lock onto the same footage
                    instead of vanishing. Zoomed, they are display-only (CSS
                    pointer-events: none) so they never block drag-to-pan; tapping
                    to (re)frame stays a Wide-only gesture. */}
                {subjects.map((s) => {
                  const screen = subjectRectToScreenPct(s.rect, focusCrop);
                  const style = {
                    left: `${screen.leftPct}%`,
                    top: `${screen.topPct}%`,
                    width: `${screen.widthPct}%`,
                    height: `${screen.heightPct}%`,
                  };
                  if (focusZoomed) {
                    return (
                      <span
                        key={s.id}
                        className="focus-subject-box is-tracking"
                        data-testid={`focus-subject-${s.id}`}
                        data-subject-id={s.id}
                        aria-hidden
                        style={style}
                      >
                        <span className="focus-subject-label">{s.label}</span>
                      </span>
                    );
                  }
                  return (
                    <button
                      key={s.id}
                      type="button"
                      className="focus-subject-box"
                      data-testid={`focus-subject-${s.id}`}
                      data-subject-id={s.id}
                      aria-label={`Frame on ${s.label}`}
                      title={`Frame on ${s.label}`}
                      style={style}
                      onClick={() => frameSubject(s)}
                    >
                      <span className="focus-subject-label">{s.label}</span>
                    </button>
                  );
                })}
                {focusZoomed && (
                  <button
                    type="button"
                    className="focus-subject-wide"
                    data-testid="focus-subject-wide"
                    aria-label="Wide: clear the frame to the full shot"
                    title="Wide (full frame)"
                    onClick={clearFocusFrame}
                  >
                    Wide
                  </button>
                )}
              </div>
            )}
            <div className="video-transport" data-testid="transport">
              <button
                type="button"
                className="video-transport-play"
                data-testid="transport-play"
                data-playing={session.playing ? "1" : "0"}
                aria-label={session.playing ? "Pause" : "Play"}
                onClick={() => void (session.playing ? session.pause() : session.play())}
              >
                {session.playing ? "Pause" : "Play"}
              </button>
              <input
                type="range"
                className="video-transport-scrub"
                data-testid="timeline-scrubber"
                data-tick={session.tick}
                min={0}
                max={Math.max(0, session.timeline.durationTicks - 1)}
                value={Math.min(
                  dragTick ?? session.tick,
                  Math.max(0, session.timeline.durationTicks - 1),
                )}
                onChange={(e) => onScrub(Number(e.target.value))}
                aria-label="Timeline scrubber"
              />
              <span className="time" data-testid="edit-duration">
                {formatTicksAsTime(session.tick, session.pack.tickRate)} /{" "}
                {formatTicksAsTime(session.timeline.durationTicks, session.pack.tickRate)}
              </span>
            </div>
          </div>
          {session.timeline.segments.length === 0 && (
            <p className="empty timeline-empty" data-testid="timeline-empty">
              All beats omitted, nothing to play.
            </p>
          )}
          {session.error && <p className="error">{session.error}</p>}
          {session.exportNote && <p className="export-note">{session.exportNote}</p>}
        </main>
      </div>

      <div className="desk-body creator-body editor-scroll" data-testid="editor-scroll">
        <section className="creator-dock-panel" data-testid="tool-sheet">
          {toolDock}
          <div className="sheet-body inspector-body" data-testid="inspector">
            {toolBody}
          </div>
        </section>
      </div>

      <div className="editor-publish-bar">
        {publishError ? (
          <p className="publish-error" data-testid="publish-error" role="alert">
            {publishError}
          </p>
        ) : null}
        <button
          type="button"
          className="primary publish-btn publish-cta"
          data-testid="publish-reel"
          disabled={constraintViolations.length > 0}
          onClick={publish}
        >
          Publish
        </button>
      </div>

      <dialog
        ref={sourceDialogRef}
        className="source-drawer"
        data-testid="source-bin"
        aria-labelledby="source-notes-title"
        onClose={() => {
          setSourceOpen(false);
          queueMicrotask(() => {
            const opener = sourceOpenerRef.current;
            if (opener && typeof opener.focus === "function") {
              // Opener may sit inside a closed <details>; still try, then fall back.
              opener.focus();
              if (document.activeElement !== opener) {
                document
                  .querySelector<HTMLElement>('[data-testid="editor-overflow"] summary')
                  ?.focus();
              }
            }
          });
        }}
        onCancel={(e) => {
          e.preventDefault();
          setSourceOpen(false);
        }}
        onClick={(e) => {
          if (e.target === sourceDialogRef.current) setSourceOpen(false);
        }}
        onKeyDown={(e) => {
          if (e.key !== "Tab") return;
          const dialog = sourceDialogRef.current;
          if (!dialog) return;
          const focusable = Array.from(
            dialog.querySelectorAll<HTMLElement>(
              'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
            ),
          ).filter((el) => !el.hasAttribute("hidden"));
          if (focusable.length === 0) {
            e.preventDefault();
            dialog.focus();
            return;
          }
          const first = focusable[0]!;
          const last = focusable[focusable.length - 1]!;
          if (focusable.length === 1) {
            e.preventDefault();
            first.focus();
          } else if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }}
      >
        <header className="source-drawer-head">
          <h2 id="source-notes-title">Source notes</h2>
          <button
            type="button"
            data-testid="close-source-notes"
            onClick={() => setSourceOpen(false)}
            aria-label="Close source notes"
          >
            Close
          </button>
        </header>
        <p className="brief">{session.pack.canonicalRecord.summary}</p>
        <ol className="source-beat-cards">
          {session.pack.beats.map((b) => (
            <li key={b.id} className="source-beat-card">
              <strong>{b.label}</strong>
              <span>{beatSeconds(b.startTick, b.endTick)}</span>
            </li>
          ))}
        </ol>
      </dialog>

      <StatsPanel
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        pack={session.pack}
        score={session.score}
        resolved={session.resolved}
        jumpCuts={jumpCuts}
        seamlessTransitions={seamlessTransitions}
        precisionTrimmedBeatIds={effectiveTrimmedBeats}
        activeEffectIds={distinctEffectIds(activeEffects)}
        captionPlacement={captionPlacementZone}
        chosenCrowdId={edit?.chosenCrowdId}
        comboLabel={comboLabel}
        returnFocusRef={studioBagBtnRef}
        spiderPinned={spiderPinned}
        onTogglePin={() => setSpiderPinned((p) => !p)}
        precisionTrim={precisionTrim}
        onToggleTrim={toggleBeatTrim}
        onOpenTrimStore={() => {
          setInfoOpen(false);
          openStore({ tool: "cut", itemId: "precision-trim" });
        }}
      />

      <StudioStoreDrawer
        open={storeOpen}
        onClose={() => setStoreOpen(false)}
        cash={state.economy.cash}
        purchases={state.purchases}
        equippedTheme={state.equippedTheme}
        focusItemId={storeFocusItemId}
        focusTool={storeFocusTool}
        returnFocusRef={storeReturnFocusRef}
        returnFocusSelector={storeReturnFocusSelector}
        purchasesDisabled={inChallenge}
        onPurchase={(itemId) => dispatch({ type: "PURCHASE", itemId })}
        onEquipTheme={(theme) => dispatch({ type: "EQUIP_THEME", theme })}
      />

      {showPerf && (
        <PerfPanel
          sceneReady={session.ready}
          assetLoadMs={assetLoadMs}
          playing={session.playing}
          onEnsurePlaying={() => void session.play()}
          packId={session.packId}
        />
      )}

      {/* touch flag retained for layout class parity */}
      <span className="sr-only" data-touch={touch ? "1" : "0"} />
    </div>
    </StudioStoreProvider>
  );
}
